import * as Sentry from '@sentry/nextjs';
import {
  useStoreVetterAnswerMutation,
  ScriptAnswerInput,
} from '@vette/data-access';
import { FormInstance } from 'antd';
import { useCallback } from 'react';
import { Form } from '../common';

export type AnswersFormType = Record<string, string[]>;

/*
1. If you provide "sessionId" then answer will be stored via mutation
2. Do not forget to set right props <FormItem name={question.id.toString()} /> for correct behavior
*/

export const useAnswersSubForm = ({
  form,
  fieldName,
  sessionId,
}: {
  form: FormInstance;
  fieldName: string;
  sessionId?: string;
}) => {
  /* We are creating subform only for answers */
  const [answersForm] = Form.useForm<AnswersFormType>();
  const [storeOneAnswer] = useStoreVetterAnswerMutation();

  const handleAnswersFormChange = useCallback(
    async (changedValues: AnswersFormType, allValues: AnswersFormType) => {
      if (sessionId) {
        await Promise.all(
          Object.entries(changedValues).map(async ([key, value]) => {
            await storeOneAnswer({
              variables: {
                sessionId,
                input: {
                  scriptQuestionId: parseInt(key),
                  answer: value,
                } satisfies ScriptAnswerInput,
              },
            }).catch(e => {
              Sentry.captureException(e);
              console.error(e);
            });
          })
        );
      }

      /* Set subform result to the field at main form instance*/
      const answers = Object.entries(allValues)
        .map(([key, value]) => ({
          scriptQuestionId: parseInt(key),
          answer: value,
        }))
        .filter(({ answer }) => answer !== undefined);

      form.setFieldsValue({ [fieldName]: answers });
      return answers;
    },
    [sessionId, storeOneAnswer, fieldName, form]
  );

  return { answersForm, handleAnswersFormChange };
};
